/**
 * DailyPlanReminderBanner · R138bj · Feb 2026
 * ────────────────────────────────────────────
 * Persistent strip below the app header that nudges the user to:
 *   1. Finish planning today (planned + submitted < daily target)
 *   2. Submit their timesheet (planned ok, but submitted < target later in day)
 *
 * The banner hides once the user has submitted enough hours to hit
 * their daily target (`submitted_ok`).
 *
 * Message shifts intelligently based on local clock:
 *   • Before 12:00        — plain "plan your day" nudge (blue-ish)
 *   • 12:00 – 17:00       — "half day gone, plan / submit"
 *   • 17:00 – 20:00       — urgent submit reminder (red)
 *   • After 20:00         — "day's ending, wrap up your timesheet"
 *
 * Data source: GET /timesheet/today-status.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Clock3, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { pollWhenVisible } from "@/lib/pollWhenVisible";

const DISMISS_STORAGE_KEY = "acq_daily_plan_dismiss_v1";

// R138bn · Dismiss lives only for the current session, not across
// reloads — users should always see the reminder the next time they
// open the app. `sessionStorage` clears on tab close but survives
// SPA route changes.
function readDismissedFor(date) {
  try {
    const raw = sessionStorage.getItem(DISMISS_STORAGE_KEY);
    if (!raw) return false;
    return raw === date;
  } catch { return false; }
}
function setDismissedFor(date) {
  try { sessionStorage.setItem(DISMISS_STORAGE_KEY, date); } catch { /* ignore */ }
}

export default function DailyPlanReminderBanner({ navOverrides = null } = {}) {
  const { activeWorkspaceId } = useAuth();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  // R158w · Feb 2026 — Hide the banner entirely when `header-timesheet-warning`
  // nav override is set to "hide" (e.g. for clients / partners who don't own a
  // timesheet obligation but log into the workspace).
  const forceHidden = navOverrides?.["header-timesheet-warning"] === "hide";

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    try {
      const { data } = await api.get(
        `/workspaces/${activeWorkspaceId}/timesheet/today-status`);
      setStatus(data);
      setDismissed(readDismissedFor(data.date));
    } catch { /* silent — banner is best-effort */ }
  }, [activeWorkspaceId]);

  useEffect(() => {
    load();
    // Refresh every 5 minutes so the message shifts as the day progresses
    // (and reflects new submissions immediately after the next open of
    // the timesheet widget). Feb 2026 R281 · Visibility-gated so
    // background tabs stop hitting; the separate visibilitychange
    // handler below is redundant now but left as-is for defensive
    // catch-up on tab focus.
    return pollWhenVisible(load, 5 * 60 * 1000);
  }, [load]);

  // Also refresh when the tab regains focus.
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [load]);

  const view = useMemo(() => {
    if (!status) return null;
    if (status.submitted_ok) return null;                // all done, hide
    // Compute local-time context
    const now = new Date();
    const hour = now.getHours();
    const isWeekend = [0, 6].includes(now.getDay());
    if (isWeekend) return null;                            // skip weekends

    const {
      planned_hours = 0, submitted_hours = 0, total_hours = 0,
      daily_target_hours = 8, planned_ok,
    } = status;
    // Oct 2026 · Round to 2 decimals so floating-point noise like
    // `2.7699999999999996` is never shown in the banner. We format for
    // display, not business logic.
    const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
    const gap = r2(Math.max(0, daily_target_hours - total_hours));

    // Decide mode + tone
    let mode = "plan";
    let tone = "red"; // user spec: planning-incomplete banner is red
    let title = "";
    let cta = "";
    let gapHours = gap; // hours left to plan (mode=plan) or submit (mode=submit)
    if (!planned_ok) {
      // R138bj · Planning phase — user hasn't blocked enough hours yet.
      // Always red per user spec. Wording sharpens as the day progresses.
      mode = "plan";
      tone = "red";
      if (hour < 12) {
        title = `Plan your day — ${gap}h short of ${daily_target_hours}h capacity`;
        cta = "Open timesheet to plan blocks";
      } else if (hour < 17) {
        title = `Half day gone — still ${gap}h unplanned of ${daily_target_hours}h`;
        cta = "Add planned blocks now";
      } else {
        title = `End of day — ${gap}h still unplanned of ${daily_target_hours}h`;
        cta = "Log the hours you worked today";
      }
    } else {
      // R138bj · Plan is complete — nudge them to convert planned blocks
      // into submitted entries. Tone escalates from amber → red as the
      // clock ticks past working hours.
      mode = "submit";
      const submitGap = r2(Math.max(0, daily_target_hours - submitted_hours));
      gapHours = submitGap;
      if (hour < 12) {
        tone = "amber";
        title = `You planned ${r2(planned_hours)}h. Submit blocks as you finish work.`;
        cta = "Open timesheet";
      } else if (hour < 17) {
        tone = "amber";
        title = `${r2(submitted_hours)}h submitted of ${daily_target_hours}h — keep the timesheet updated.`;
        cta = "Submit today's blocks";
      } else if (hour < 20) {
        tone = "red";
        title = `Submit your timesheet — ${submitGap}h still in draft.`;
        cta = "Submit now";
      } else {
        tone = "rose";
        title = `Day is ending — please submit ${submitGap}h before signing off.`;
        cta = "Submit timesheet";
      }
    }
    return { mode, tone, title, cta, gap: Math.round(gapHours * 10) / 10 };
  }, [status]);

  if (!view || dismissed || forceHidden) return null;

  const toneClass =
    view.tone === "red"
      ? "bg-red-600 text-white border-red-700"
      : view.tone === "rose"
        ? "bg-rose-600 text-white border-rose-700"
        : "bg-amber-500 text-black border-amber-600";

  // R138bj · CTA target depends on mode:
  //   • plan   → dispatch global "open-daily-plan" event so the
  //     workspace-wide Daily Plan dialog opens in place.
  //   • submit → navigate to the full Timesheet page (submit workflow
  //     needs the fuller surface — swimlane, approvals, reports).
  const openDailyPlan = () => {
    try { window.dispatchEvent(new CustomEvent("open-daily-plan")); }
    catch { /* ignore */ }
  };

  return (
    <div
      className={`w-full border-b-2 px-3 sm:px-6 py-1.5 flex items-center gap-2 text-xs font-semibold ${toneClass}`}
      data-testid={`daily-plan-banner-${view.mode}`}
      data-tone={view.tone}
    >
      {view.tone === "red" || view.tone === "rose"
        ? <AlertTriangle className="w-4 h-4 shrink-0" />
        : <Clock3 className="w-4 h-4 shrink-0" />}
      {/* R138bn · Live counter chip — shows the exact remaining hours
          so users see the shortfall at a glance without opening the
          Daily Plan popup. Hidden when there's nothing left to log.
          Feb 2026 R291g · `whitespace-nowrap` + `shrink-0` keeps the
          chip on a single line even on narrow phones (was wrapping
          "8h LEFT TO PLAN" onto three lines before).  */}
      {view.gap > 0 && (
        <span
          className="inline-flex items-baseline gap-1 px-2 py-0.5 border border-current bg-white/20 font-mono tabular-nums whitespace-nowrap shrink-0"
          data-testid="daily-plan-banner-counter"
          data-gap-hours={view.gap}
        >
          <span className="text-sm font-bold leading-none">{view.gap % 1 === 0 ? view.gap : view.gap.toFixed(1)}h</span>
          <span className="hidden sm:inline text-[9px] uppercase tracking-widest opacity-80 leading-none">
            {view.mode === "plan" ? "left to plan" : "left to submit"}
          </span>
        </span>
      )}
      <span className="truncate min-w-0 flex-1">{view.title}</span>
      <div className="ml-auto flex items-center gap-2 shrink-0">
        {view.mode === "plan" ? (
          <button
            type="button"
            onClick={openDailyPlan}
            className="text-[10px] uppercase tracking-widest px-2 py-1 border border-current bg-white/15 hover:bg-white/25 transition whitespace-nowrap shrink-0"
            data-testid="daily-plan-banner-cta"
          >
            <span className="hidden sm:inline">{view.cta} →</span>
            <span className="sm:hidden">Plan →</span>
          </button>
        ) : (
          <Link
            to="/app/timesheet"
            className="text-[10px] uppercase tracking-widest px-2 py-1 border border-current bg-white/15 hover:bg-white/25 transition whitespace-nowrap shrink-0"
            data-testid="daily-plan-banner-cta"
          >
            <span className="hidden sm:inline">{view.cta} →</span>
            <span className="sm:hidden">Submit →</span>
          </Link>
        )}
        <button
          type="button"
          onClick={() => {
            // R138bl · Only allow manual dismiss for the *submit* mode.
            // Planning-incomplete state must stay pinned so users can't
            // hide the reminder before they've allocated their day.
            if (view.mode === "plan") return;
            if (status?.date) setDismissedFor(status.date);
            setDismissed(true);
          }}
          className={`opacity-80 hover:opacity-100 ${view.mode === "plan" ? "hidden" : ""}`}
          title="Dismiss for today"
          data-testid="daily-plan-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
